import apiClient from './client';

/* ---------- Types ---------- */

export interface ModelConfig {
  id: string;
  path: 'public' | 'private';
  profile: 'dev' | 'uat' | 'prod';
  backend_type: 'cloud' | 'local' | 'openai' | 'anthropic' | 'claude_code' | 'pi_agent';
  endpoint: string | null;
  model_name: string;
  api_key_ref: string | null;
  api_key_masked?: string;
  has_api_key?: boolean;
  is_active: boolean;
  self_hosted?: boolean;
  // backend returns this when a config saves but trips a soft
  // guard-rail warning (e.g. private path set to a hosted/cloud model).
  guard_rail_warning?: string | null;
}

export interface DataSource {
  id: string;
  source: string;
  display_name: string;
  description: string;
  source_url: string;
  egress_enabled: boolean;
  per_source_toggleable?: boolean;
}

export interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  role: 'analyst' | 'reviewer' | 'principal' | 'compliance' | 'operator';
  is_active: boolean;
  can_see_all_cases?: boolean;
  settings?: Record<string, unknown> | null;
}

/* ---------- Model Config ---------- */

export async function getModelConfigs(): Promise<ModelConfig[]> {
  const { data } = await apiClient.get<{ items: ModelConfig[] }>('/admin/model-config');
  return data.items;
}

export interface UpsertModelConfigInput {
  path: 'public' | 'private';
  profile: 'dev' | 'uat' | 'prod';
  backend_type: 'cloud' | 'local' | 'openai' | 'anthropic' | 'claude_code' | 'pi_agent';
  endpoint?: string;
  model_name: string;
  api_key_ref?: string;
  api_key?: string;
  clear_api_key?: boolean;
  self_hosted?: boolean;
}

export async function upsertModelConfig(input: UpsertModelConfigInput): Promise<ModelConfig> {
  const { data } = await apiClient.post<ModelConfig>('/admin/model-config', input);
  return data;
}

export interface ModelConfigTestResult {
  ok: boolean;
  message: string;
  latency_ms: number | null;
}

/** Probe a model configuration with a tiny LLM call. Sends the
 * same fields as upsert; when api_key is omitted the backend reuses the
 * stored key for (path, profile). */
export async function testModelConfig(input: UpsertModelConfigInput): Promise<ModelConfigTestResult> {
  const { data } = await apiClient.post<ModelConfigTestResult>('/admin/model-config/test', input);
  return data;
}

/* ---------- Claude Agent Activation ---------- */

export interface ClaudeAuthStatus {
  authenticated: boolean;
  auth_method: string | null;
  token_expires_at: number | null;
  creds_file_present: boolean;
  token_present?: boolean;
  account?: string | null;
  org_name?: string | null;
  subscription_type?: string | null;
  message?: string | null;
}

export interface ClaudeActivateStartResult {
  handle: string;
  auth_url: string | null;
  code: string | null;
  flow: string;
}

export interface ClaudeActivateStatusResult {
  handle: string;
  status: 'pending' | 'complete' | 'error' | 'unknown';
  message?: string | null;
  creds_file_present: boolean;
  flow: string;
}

export async function getClaudeAuthStatus(): Promise<ClaudeAuthStatus> {
  const { data } = await apiClient.get<ClaudeAuthStatus>('/admin/claude/auth-status');
  return data;
}

export async function startClaudeActivation(): Promise<ClaudeActivateStartResult> {
  const { data } = await apiClient.post<ClaudeActivateStartResult>('/admin/claude/activate');
  return data;
}

export async function getClaudeActivationStatus(handle: string): Promise<ClaudeActivateStatusResult> {
  const { data } = await apiClient.get<ClaudeActivateStatusResult>(
    '/admin/claude/activate/status', { params: { handle } },
  );
  return data;
}

export async function completeClaudeActivation(handle: string, code: string): Promise<ClaudeActivateStatusResult> {
  const { data } = await apiClient.post<ClaudeActivateStatusResult>(
    '/admin/claude/activate/complete', { handle, code },
  );
  return data;
}

export async function claudeLogout(): Promise<{ ok: boolean; message: string }> {
  const { data } = await apiClient.post<{ ok: boolean; message: string }>('/admin/claude/logout');
  return data;
}

/* ---------- System Settings (LLM concurrency) ---------- */

export interface SystemSetting {
  id: string;
  key: string;
  value: string | null;
  value_int: number | null;
  updated_by: string | null;
  updated_at: string | null;
}

export async function getSystemSettings(): Promise<SystemSetting[]> {
  const { data } = await apiClient.get<{ items: SystemSetting[] }>('/admin/system-settings');
  return data.items;
}

export async function updateSystemSetting(key: string, value: number | boolean | string): Promise<SystemSetting> {
  const { data } = await apiClient.put<SystemSetting>(`/admin/system-settings/${key}`, { value });
  return data;
}

/* ---------- Security: PII strip toggle + document encryption ---------- */

export interface EncryptExistingResult {
  encrypted: number;
  skipped: number;
  errors: number;
  error_details?: string[];
}

export async function encryptExistingDocs(): Promise<EncryptExistingResult> {
  const { data } = await apiClient.post<EncryptExistingResult>('/admin/docs/encrypt-existing');
  return data;
}

/* PII-strip guard lock. When the private path uses a cloud
   backend (anthropic / claude_code / pi_agent), the cloud_llm_pii_strip
   toggle is forced ON + non-editable in the admin UI + Tools section. */
export interface PiiStripLock {
  locked: boolean;
  private_backend_type: string | null;
  reason: string | null;
}

export async function getPiiStripLock(): Promise<PiiStripLock> {
  const { data } = await apiClient.get<PiiStripLock>('/admin/pii-strip-lock');
  return data;
}

/* ---------- Data Sources ---------- */

export async function getDataSources(): Promise<DataSource[]> {
  // The API doesn't have a dedicated data-sources endpoint yet; we derive
  // status from model-config / admin endpoints. For now we use a local
  // representation that the operator can toggle (persisted via model-config
  // egress settings or a future /admin/data-sources endpoint).
  const { data } = await apiClient.get<{ items: DataSource[] }>('/admin/data-sources');
  return data.items;
}

export async function toggleDataSource(sourceId: string, enabled: boolean): Promise<DataSource> {
  const { data } = await apiClient.patch<DataSource>(`/admin/data-sources/${sourceId}`, {
    egress_enabled: enabled,
  });
  return data;
}

/* ---------- Users ---------- */

export async function getAdminUsers(): Promise<AdminUser[]> {
  const { data } = await apiClient.get<{ items: AdminUser[] }>('/admin/users');
  return data.items;
}

export interface CreateUserInput {
  email: string;
  full_name: string;
  role: 'analyst' | 'reviewer' | 'principal' | 'compliance' | 'operator';
  password: string;
  can_see_all_cases?: boolean;
}

export async function createUser(input: CreateUserInput): Promise<AdminUser> {
  const { data } = await apiClient.post<AdminUser>('/admin/users', input);
  return data;
}

export interface UpdateUserInput {
  full_name?: string;
  role?: 'analyst' | 'reviewer' | 'principal' | 'compliance' | 'operator';
  is_active?: boolean;
  can_see_all_cases?: boolean;
  settings?: Record<string, unknown> | null;
}

export async function updateUser(userId: string, input: UpdateUserInput): Promise<AdminUser> {
  const { data } = await apiClient.patch<AdminUser>(`/admin/users/${userId}`, input);
  return data;
}
/* ---------- Egress Log ( compliance audit) ---------- */

export interface EgressLogEntry {
  id: string;
  ts: string;
  category: string;
  destination_host: string;
  destination_url: string;
  method: string;
  request_payload: Record<string, unknown> | null;
  request_payload_truncated?: boolean;
  response_status: number | null;
  response_size: number | null;
  response_meta?: Record<string, unknown> | null;
  success: boolean;
  duration_ms: number | null;
  source_module: string;
  case_id: string | null;
  sensitive: boolean;
  error: string | null;
}

export interface EgressLogListResponse {
  items: EgressLogEntry[];
  total: number;
  page: number;
  limit: number;
}

export interface EgressLogQuery {
  category?: string;
  destination_host?: string;
  case_id?: string;
  sensitive?: boolean;
  since?: string;
  q?: string;
  page?: number;
  limit?: number;
}

export async function getEgressLog(query: EgressLogQuery = {}): Promise<EgressLogListResponse> {
  const params: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params[k] = v;
  }
  const { data } = await apiClient.get<EgressLogListResponse>('/admin/egress-log', { params });
  return data;
}

/** Fetch a single egress-log entry with the FULL request_payload (the list
 * endpoint truncates large payloads for page size; compliance officers
 * fetch the full payload on demand when expanding a row). */
export async function getEgressLogEntry(id: string): Promise<EgressLogEntry> {
  const { data } = await apiClient.get<EgressLogEntry>(`/admin/egress-log/${id}`);
  return data;
}

/* ----------  part 2b: configurable PII rules ---------- */

export interface PiiRule {
  id: string;
  name: string;
  category: string;
  pattern: string;
  replacement: string;
  enabled: boolean;
  priority: number;
  group_index: number;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface PiiRuleInput {
  name: string;
  category: string;
  pattern: string;
  replacement?: string;
  enabled?: boolean;
  priority?: number;
  group_index?: number;
}

export interface PiiRuleTestRedaction {
  category: string;
  count: number;
}

export interface PiiRuleTestResult {
  redacted_text: string;
  redactions: PiiRuleTestRedaction[];
}

export async function getPiiRules(): Promise<PiiRule[]> {
  const { data } = await apiClient.get<{ items: PiiRule[] }>('/admin/pii-rules');
  return data.items;
}

export async function createPiiRule(input: PiiRuleInput): Promise<PiiRule> {
  const { data } = await apiClient.post<PiiRule>('/admin/pii-rules', input);
  return data;
}

export async function updatePiiRule(id: string, input: Partial<PiiRuleInput>): Promise<PiiRule> {
  const { data } = await apiClient.patch<PiiRule>(`/admin/pii-rules/${id}`, input);
  return data;
}

export async function deletePiiRule(id: string): Promise<{ deleted: boolean; id: string }> {
  const { data } = await apiClient.delete<{ deleted: boolean; id: string }>(`/admin/pii-rules/${id}`);
  return data;
}

export async function testPiiRules(text: string, ruleIds?: string[]): Promise<PiiRuleTestResult> {
  const { data } = await apiClient.post<PiiRuleTestResult>('/admin/pii-rules/test', {
    text,
    rule_ids: ruleIds ?? null,
  });
  return data;
}

export interface PiiPatternTestResult {
  redacted_text: string;
  matches: { category: string; count: number }[];
  match_count: number;
  valid: boolean;
}

export async function testPiiPattern(body: {
  pattern: string;
  text: string;
  group_index?: number;
  replacement?: string;
}): Promise<PiiPatternTestResult> {
  const { data } = await apiClient.post<PiiPatternTestResult>('/admin/pii-rules/test-pattern', body);
  return data;
}

export interface PiiRegexGenerateResult {
  valid: boolean;
  pattern: string | null;
  explanation: string | null;
  error: string | null;
}

export async function generatePiiRegex(body: {
  description: string;
  samples?: string;
  hints?: string;
}): Promise<PiiRegexGenerateResult> {
  const { data } = await apiClient.post<PiiRegexGenerateResult>('/admin/pii-rules/generate-regex', body);
  return data;
}

// LLM field configuration — which case-fact fields are blocked from AI
export interface LLMFieldConfig {
  field: string;
  blocked: boolean;
}

export async function getLLMFieldConfig(): Promise<{ fields: LLMFieldConfig[] }> {
  const { data } = await apiClient.get('/admin/llm-field-config');
  return data;
}

export async function updateLLMFieldConfig(blockedFields: string[]): Promise<{ blocked_fields: string[]; count: number }> {
  const { data } = await apiClient.put('/admin/llm-field-config', { blocked_fields: blockedFields });
  return data;
}


// ===========================================================================
// Configurable deficiency templates (UC-6.5)
// ===========================================================================

export interface DeficiencyRule {
  id: string;
  code: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
  enabled: boolean;
  priority: number;
  created_at?: string;
  updated_at?: string;
  updated_by?: string | null;
}

export interface DeficiencyRuleInput {
  code: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
  enabled?: boolean;
  priority?: number;
}

export async function getDeficiencyRules(): Promise<DeficiencyRule[]> {
  const { data } = await apiClient.get<{ items: DeficiencyRule[] }>('/admin/deficiency-rules');
  return data.items;
}

export async function createDeficiencyRule(input: DeficiencyRuleInput): Promise<DeficiencyRule> {
  const { data } = await apiClient.post<DeficiencyRule>('/admin/deficiency-rules', input);
  return data;
}

export async function updateDeficiencyRule(id: string, input: Partial<DeficiencyRuleInput>): Promise<DeficiencyRule> {
  const { data } = await apiClient.put<DeficiencyRule>(`/admin/deficiency-rules/${id}`, input);
  return data;
}

export async function deleteDeficiencyRule(id: string): Promise<{ deleted: boolean; id: string }> {
  const { data } = await apiClient.delete<{ deleted: boolean; id: string }>(`/admin/deficiency-rules/${id}`);
  return data;
}


// ===========================================================================
// Configurable checklist rules (required documents + required information)
// ===========================================================================

export interface RequiredDocumentRule {
  id: string;
  case_type: string | null;
  depositor_type: string | null;
  label: string;
  description: string;
  match_doc_category: string | null;
  match_classification: string | null;
  match_keywords: string | null;
  enabled: boolean;
  priority: number;
  created_at?: string;
  updated_at?: string;
  updated_by?: string | null;
}

export interface RequiredDocumentRuleInput {
  case_type?: string | null;
  depositor_type?: string | null;
  label: string;
  description?: string;
  match_doc_category?: string | null;
  match_classification?: string | null;
  match_keywords?: string | null;
  enabled?: boolean;
  priority?: number;
}

export async function getRequiredDocumentRules(): Promise<RequiredDocumentRule[]> {
  const { data } = await apiClient.get<{ items: RequiredDocumentRule[] }>('/admin/required-document-rules');
  return data.items;
}
export async function createRequiredDocumentRule(input: RequiredDocumentRuleInput): Promise<RequiredDocumentRule> {
  const { data } = await apiClient.post<RequiredDocumentRule>('/admin/required-document-rules', input);
  return data;
}
export async function updateRequiredDocumentRule(id: string, input: Partial<RequiredDocumentRuleInput>): Promise<RequiredDocumentRule> {
  const { data } = await apiClient.put<RequiredDocumentRule>(`/admin/required-document-rules/${id}`, input);
  return data;
}
export async function deleteRequiredDocumentRule(id: string): Promise<{ deleted: boolean; id: string }> {
  const { data } = await apiClient.delete<{ deleted: boolean; id: string }>(`/admin/required-document-rules/${id}`);
  return data;
}

export interface RequiredInformationRule {
  id: string;
  case_type: string | null;
  label: string;
  description: string;
  match_field: string | null;
  match_keywords: string | null;
  enabled: boolean;
  priority: number;
  created_at?: string;
  updated_at?: string;
  updated_by?: string | null;
}

export interface RequiredInformationRuleInput {
  case_type?: string | null;
  label: string;
  description?: string;
  match_field?: string | null;
  match_keywords?: string | null;
  enabled?: boolean;
  priority?: number;
}

export async function getRequiredInformationRules(): Promise<RequiredInformationRule[]> {
  const { data } = await apiClient.get<{ items: RequiredInformationRule[] }>('/admin/required-information-rules');
  return data.items;
}
export async function createRequiredInformationRule(input: RequiredInformationRuleInput): Promise<RequiredInformationRule> {
  const { data } = await apiClient.post<RequiredInformationRule>('/admin/required-information-rules', input);
  return data;
}
export async function updateRequiredInformationRule(id: string, input: Partial<RequiredInformationRuleInput>): Promise<RequiredInformationRule> {
  const { data } = await apiClient.put<RequiredInformationRule>(`/admin/required-information-rules/${id}`, input);
  return data;
}
export async function deleteRequiredInformationRule(id: string): Promise<{ deleted: boolean; id: string }> {
  const { data } = await apiClient.delete<{ deleted: boolean; id: string }>(`/admin/required-information-rules/${id}`);
  return data;
}
